import { useState, useEffect } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function BROSInvoiceProbePanel({ initialBrosId } = {}) {
  const [brosId, setBrosId] = useState(initialBrosId || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-probe when initialBrosId is provided (e.g. from URL query param)
  useEffect(() => {
    if (initialBrosId && initialBrosId.trim()) {
      setBrosId(initialBrosId);
      // Trigger probe on next tick so state is settled
      const doProbe = async () => {
        setLoading(true);
        setResult(null);
        try {
          const { data } = await diagnosticsAPI.brosInvoiceProbe(initialBrosId.trim());
          setResult(data);
        } catch (err) {
          toast.error(getErrorMessage(err));
        } finally {
          setLoading(false);
        }
      };
      doProbe();
    }
  }, [initialBrosId]);

  const runProbe = async () => {
    const id = brosId.trim();
    if (!id) {
      toast.error('Enter a BROS invoice ID');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosInvoiceProbe(id);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') runProbe();
  };

  const comparison = result?.comparison;
  const statusCandidates = result?.api_status_candidates;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Invoice Probe</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Compare a single invoice between the BR OS API (live) and our local DB.
          Identifies field-name mismatches (e.g. status vs payment_status).
        </p>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={brosId}
            onChange={(e) => setBrosId(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="BROS invoice ID"
            className="border rounded px-3 py-1.5 text-sm w-48 bg-background"
          />
          <Button onClick={runProbe} disabled={loading || !brosId.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Probe
          </Button>
        </div>

        {result?.api_error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" />
            API error: {result.api_error}
          </div>
        )}

        {result && !result.api_error && (
          <div className="space-y-4">
            {/* Status candidates from API (the smoking-gun read) */}
            {statusCandidates && Object.keys(statusCandidates).length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">API status-related fields</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Key</th>
                        <th className="text-left px-2 py-1.5 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(statusCandidates).map(([k, v]) => (
                        <tr key={k} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{k}</td>
                          <td className="px-2 py-1 font-mono">
                            <Badge variant={
                              String(v).toLowerCase() === 'paid' ? 'default' : 'outline'
                            }>
                              {String(v)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Comparison diffs */}
            {comparison && (
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  {comparison.match ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      API and DB match on checked fields
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      {comparison.diffs.length} field(s) differ between API and DB
                    </>
                  )}
                </p>
                {comparison.diffs.length > 0 && (
                  <div className="overflow-x-auto border border-amber-200 dark:border-amber-800 rounded-lg">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-amber-50/50 dark:bg-amber-950/30">
                          <th className="text-left px-2 py-1.5 font-medium">Field</th>
                          <th className="text-left px-2 py-1.5 font-medium">API value</th>
                          <th className="text-left px-2 py-1.5 font-medium">DB value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.diffs.map((d) => (
                          <tr key={d.field} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono font-medium">{d.field}</td>
                            <td className="px-2 py-1 font-mono">{String(d.api_value ?? '(null)')}</td>
                            <td className="px-2 py-1 font-mono">{String(d.db_value ?? '(null)')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* All API keys */}
            {result.api_all_keys && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  All API response keys ({result.api_all_keys.length})
                </summary>
                <div className="mt-2 flex flex-wrap gap-1">
                  {result.api_all_keys.map((k) => (
                    <Badge key={k} variant="outline" className="text-[10px] font-mono">{k}</Badge>
                  ))}
                </div>
              </details>
            )}

            {/* Raw API payload */}
            {result.api_payload && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Raw API payload
                </summary>
                <pre className="mt-2 border rounded-lg p-2 overflow-x-auto bg-muted/30 text-[10px] max-h-64 overflow-y-auto">
                  {JSON.stringify(result.api_payload, null, 2)}
                </pre>
              </details>
            )}

            {/* Raw DB document */}
            {result.db_doc && (
              <details className="text-xs">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  DB document
                </summary>
                <pre className="mt-2 border rounded-lg p-2 overflow-x-auto bg-muted/30 text-[10px] max-h-64 overflow-y-auto">
                  {JSON.stringify(result.db_doc, null, 2)}
                </pre>
              </details>
            )}

            {/* Not found states */}
            {!result.api_payload && !result.api_error && (
              <p className="text-sm text-muted-foreground">
                Invoice {result.bros_id} not found in BR OS API response.
              </p>
            )}
            {!result.db_doc && (
              <p className="text-sm text-muted-foreground">
                Invoice {result.bros_id} not found in local DB.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
